import type { IncomingMessage, ServerResponse } from 'node:http';
import type { CliErrorCodeType } from '@wix/cli-error';
import { CliErrorCode } from '@wix/cli-error';
import { z } from 'zod';
import { variant, fields, match } from 'variant';
import type { VariantOf } from 'variant';

const LogMessageSchema = z.unknown();
export type LogMessage = z.infer<typeof LogMessageSchema>;

const LogMessageLevelSchema = z.enum(['info', 'error', 'warn', 'debug', 'log']);
export type LogMessageLevel = z.infer<typeof LogMessageLevelSchema>;

const LogItemSchema = z.object({
  message: z.array(LogMessageSchema),
  level: LogMessageLevelSchema,
  timestamp: z.number(),
});

type LogItem = z.infer<typeof LogItemSchema>;

const ExceptionItemSchema = z.object({
  stack: z.string(),
  name: z.string(),
  message: z.string(),
  timestamp: z.number(),
});

type ExceptionItem = z.infer<typeof ExceptionItemSchema>;

const LogResponseSchema = z
  .object({
    _type: z.literal('log'),
    exceptions: z.array(ExceptionItemSchema),
    logs: z.array(LogItemSchema),
  })
  .passthrough();

const SessionTimeoutResponseSchema = z
  .object({
    _type: z.literal('session-timeout'),
  })
  .passthrough();

const ServerLogResponseSchema = z.discriminatedUnion('_type', [
  LogResponseSchema,
  SessionTimeoutResponseSchema,
]);

type ServerLogResponse = z.infer<typeof ServerLogResponseSchema>;

const AggregatedLog = variant({
  Log: fields<LogItem>(),
  Exception: fields<ExceptionItem>(),
});
export type AggregatedLog = VariantOf<typeof AggregatedLog>;

export type OnLog = (data: AggregatedLog[]) => void;
type OnError = (
  errType: CliErrorCodeType,
  err: unknown,
  info?: Record<string, unknown>
) => void;

export const LOGS_ENDPOINT = '/logs';

export const logsReqHandler = (req: IncomingMessage, res: ServerResponse) => {
  let onLog: OnLog;
  let onError: OnError;

  if (req.method === 'POST' && req.url === LOGS_ENDPOINT) {
    let body = '';

    req.on('data', (chunk) => {
      body += chunk.toString();
    });

    req.on('end', () => {
      let parsedBody: ServerLogResponse;
      try {
        parsedBody = ServerLogResponseSchema.parse(JSON.parse(body));
      } catch (err) {
        onError(CliErrorCode.FailedToParseLog(), err, { body });
        res.statusCode = 400;
        return res.end();
      }

      const resultLogs: AggregatedLog[] = [];

      if (parsedBody._type === 'log') {
        resultLogs.push(
          ...parsedBody.exceptions.map((exception) =>
            AggregatedLog.Exception(exception)
          )
        );
        resultLogs.push(
          ...parsedBody.logs.map((log) => AggregatedLog.Log(log))
        );
      }

      match(parsedBody._type, {
        log: () => {
          onLog(resultLogs);
        },
        'session-timeout': () => {
          res.statusCode = 200;
          res.end();

          onError(CliErrorCode.LogsConnectionTimeout(), {}, { body });
        },
      });

      res.statusCode = 200;
      res.end();
    });
  } else {
    res.statusCode = 404;
    res.end('Not Found');
  }

  const api = {
    onLog: (callback: OnLog) => {
      onLog = callback;
      return api;
    },
    onError: (callback: OnError) => {
      onError = callback;
      return api;
    },
  };

  return api;
};
